import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import * as api from '@/features/canvas/api';
import { Whiteboard } from '@/features/canvas/types';
import { whiteboardPath } from '@/constants/routes';
import { isDesktopApp } from '@/features/identity/desktop-updates';
import { Button } from '@/components/ui/Button';
import { SearchField } from '@/components/ui/SearchField';
import { inputCls } from '@/components/ui/form';
import { Plus } from '@/components/ui/icons';
import { Spinner } from '@/components/ui/Spinner';
import AreaNav from '@/components/area-nav';
import IdentityMenu from '@/features/identity/identity-menu';
import { WhiteboardThumb } from '@/components/whiteboard-thumb';

/**
 * The Whiteboards area index at "/whiteboards" — a UUID-free-feeling grid of infinite-canvas
 * whiteboards. Pick one to open its full-screen canvas, search by name, or create a new one. Mirrors
 * the boards index; no board chrome (this is a top-level area).
 */
export default function WhiteboardsIndexPage() {
  const navigate = useNavigate();
  const [boards, setBoards] = useState<Whiteboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const addingRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        setBoards(await api.listWhiteboards());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const sorted = [...boards].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
    return needle ? sorted.filter((b) => b.name.toLowerCase().includes(needle)) : sorted;
  }, [boards, q]);

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed || addingRef.current) return;
    addingRef.current = true;
    setBusy(true);
    try {
      const wb = await api.createWhiteboard({ name: trimmed });
      navigate(whiteboardPath(wb.id));
    } finally {
      addingRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-fg">
      <header className="sticky top-0 z-30 border-b border-line bg-ink/80 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-[1100px] items-center justify-between gap-4 px-6">
          <div className="flex items-center gap-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand/15 text-brand">
              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M2 3.2C2 2.54 2.54 2 3.2 2h9.6c.66 0 1.2.54 1.2 1.2v6.4c0 .66-.54 1.2-1.2 1.2H9l-2.4 2.6V10.8H3.2c-.66 0-1.2-.54-1.2-1.2V3.2Z" fill="currentColor" />
              </svg>
            </span>
            <span className="text-[14px] font-semibold text-fg-strong">spark-board</span>
          </div>
          <AreaNav />
          <div className="flex items-center gap-1">
            {/* Desktop app: the account lives in the tab strip (see DesktopHeaderProxy). */}
            {isDesktopApp() ? null : <IdentityMenu />}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-6 py-10">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[22px] font-bold text-fg-strong">Whiteboards</h1>
            <p className="mt-1 text-[13px] text-fg-muted">Open an infinite canvas, or create a new one.</p>
          </div>
          <div className="flex items-center gap-2">
            <SearchField value={q} onChange={setQ} placeholder="Search whiteboards…" />
            {!creating ? (
              <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                <Plus /> New whiteboard
              </Button>
            ) : null}
          </div>
        </div>

        {creating ? (
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-line bg-surface-inset p-2">
            <input
              autoFocus
              className={inputCls}
              placeholder="Whiteboard name (e.g. Q3 Planning)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') create();
                if (e.key === 'Escape') {
                  setCreating(false);
                  setName('');
                }
              }}
            />
            <Button variant="primary" size="sm" disabled={busy || !name.trim()} onClick={create}>
              Create
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setCreating(false);
                setName('');
              }}
            >
              Cancel
            </Button>
          </div>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-24">
            <Spinner className="h-6 w-6" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-line bg-surface-raised px-6 py-16 text-center text-[13px] text-fg-faint">
            {q.trim() ? `No whiteboards match “${q.trim()}”.` : 'No whiteboards yet — create your first one above.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((b) => (
              <Link
                key={b.id}
                to={whiteboardPath(b.id)}
                className="group flex flex-col rounded-2xl border border-line bg-surface-raised p-4 transition-colors hover:border-line-strong hover:bg-layer"
              >
                <WhiteboardThumb whiteboardId={b.id} snapshotKey={b.snapshot_key ?? null} snapshotAt={b.snapshot_at} className="mb-8 h-28 w-full rounded-xl ring-1 ring-line" />
                <div className="text-[15px] font-semibold text-fg-strong group-hover:text-fg">{b.name}</div>
                <div className="mt-1 text-[11px] text-fg-faint">
                  Updated {new Date(b.updated_at).toLocaleDateString()}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
